import React, { useState, useMemo, useRef } from "react";
import { flushSync } from "react-dom";
import { Link } from "react-router-dom";
import { useVirtualizer } from "@tanstack/react-virtual";
import { typeIcons } from "./Utils";
import usePopoverPosition from "./usePopoverPosition";
import "./Table.css";
import Checkbox from './Checkbox';
import { parseDbDate } from "../utils/dates";

// Minimum pixels the table reserves per unit of column `width`. A column with
// the default weight of 10 will never be squeezed below 10 * this value.
const MIN_PX_PER_WIDTH_UNIT = 1;

const Table = ({
  data = [],
  columns = [],
  loading = false,
  emptyMessage = "No data found.",
  onRowClick = null,
  defaultSortColumn = null,
  defaultSortDirection = null,
  onProcessedDataChange = null,
  fillPage = false,
  getRowClassName = null,
  getRowSortPriority = null,
}) => {
  const [sortColumn, setSortColumn] = useState(defaultSortColumn);
  const [sortDirection, setSortDirection] = useState(
    defaultSortDirection || "none",
  ); // 'none', 'desc', 'asc'
  const [filters, setFilters] = useState({});

  // Per-column list filter match mode: 'any' (OR) or 'all' (AND)
  const [listFilterMatchMode, setListFilterMatchMode] = useState({});

  // Track which list-filter dropdown is open (by column key)
  const [openListFilterKey, setOpenListFilterKey] = useState(null);
  // Track per-column group expand/collapse state for list-filter dropdown
  const [openListFilterGroups, setOpenListFilterGroups] = useState({});
  const listFilterContainerRef = useRef(null);
  const listFilterPanelRef = useRef(null);
  // The open column filter dropdown stays on screen (shifted/flipped near the edges).
  const listFilterPanelStyle = usePopoverPosition(openListFilterKey != null, listFilterContainerRef, listFilterPanelRef, { align: "end" });
  const lastProcessedSignatureRef = useRef(null);
  const headerCellRefs = useRef([]);
  const scrollContainerRef = useRef(null);
  const [pinnedOffsets, setPinnedOffsets] = useState([]);

  // The right-most column marked `fixed` pins itself and every column to its left.
  const lastFixedIndex = useMemo(() => {
    let idx = -1;
    columns.forEach((col, i) => {
      if (col.fixed) idx = i;
    });
    return idx;
  }, [columns]);

  // Each column's rendered width is its `width` (default 10) as a percentage of the
  // sum of `width`s across the currently visible columns.
  const columnWidthPercents = useMemo(() => {
    const weights = columns.map((col) => {
      const n = Number(col.width);
      return Number.isFinite(n) && n > 0 ? n : 10;
    });
    const total = weights.reduce((sum, w) => sum + w, 0);
    if (total <= 0) return columns.map(() => 0);
    return weights.map((w) => (w / total) * 100);
  }, [columns]);

  // Below this width the table stops shrinking columns further and the
  // wrapper scrolls horizontally instead, so narrow columns stay legible.
  const tableMinWidth = useMemo(() => {
    const totalWeight = columns.reduce((sum, col) => {
      const n = Number(col.width);
      return sum + (Number.isFinite(n) && n > 0 ? n : 10);
    }, 0);
    return totalWeight * MIN_PX_PER_WIDTH_UNIT;
  }, [columns]);

  const splitPrefixedOption = (value) => {
    const text = value === null || value === undefined ? "" : String(value);
    // Group by the first " - " delimiter: "XXX - rest"
    const delimiter = " - ";
    const idx = text.indexOf(delimiter);
    if (idx <= 0) return { prefix: null, suffix: null };
    const prefix = text.slice(0, idx).trim();
    const suffix = text.slice(idx + delimiter.length).trim();
    if (!prefix || !suffix) return { prefix: null, suffix: null };
    return { prefix, suffix };
  };

  const getListItemLabel = (item, column) => {
    if (column?.getItemLabel) return column.getItemLabel(item);
    if (item === null || item === undefined) return "";
    if (typeof item === "string" || typeof item === "number")
      return String(item);
    if (typeof item === "object") {
      return String(item.name ?? item.label ?? item.value ?? item.title ?? "");
    }
    return "";
  };

  const getListItemValue = (item, column) => {
    if (column?.getItemValue) return column.getItemValue(item);
    // Default to label so filtering matches what's displayed
    return getListItemLabel(item, column);
  };

  // Close list-filter dropdown on outside click
  React.useEffect(() => {
    const handleMouseDown = (e) => {
      if (!openListFilterKey) return;
      if (!listFilterContainerRef.current) return;
      if (!listFilterContainerRef.current.contains(e.target)) {
        setOpenListFilterKey(null);
      }
    };

    document.addEventListener("mousedown", handleMouseDown);
    return () => document.removeEventListener("mousedown", handleMouseDown);
  }, [openListFilterKey]);

  // Extract unique values for discrete, participant, and list columns
  const getUniqueValues = (columnKey, columnType, sourceRows = data) => {
    if (columnType === "participant") {
      const participantSet = new Set();
      (Array.isArray(sourceRows) ? sourceRows : []).forEach((row) => {
        const participants = row[columnKey] || [];
        participants.forEach((p) => {
          const displayName =
            p.type === "contact"
              ? `${p.first_name || ""} ${p.last_name || ""}`.trim()
              : p.name_short;
          if (displayName) participantSet.add(displayName);
        });
      });
      return Array.from(participantSet).sort();
    } else if (columnType === "discrete") {
      const valueSet = new Set();
      (Array.isArray(sourceRows) ? sourceRows : []).forEach((row) => {
        const value = row[columnKey];
        if (value !== null && value !== undefined && value !== "")
          valueSet.add(value);
      });
      return Array.from(valueSet).sort();
    } else if (columnType === "list") {
      const valueSet = new Set();
      const column = columns.find((col) => col.key === columnKey);
      (Array.isArray(sourceRows) ? sourceRows : []).forEach((row) => {
        const list = row[columnKey];
        if (!Array.isArray(list)) return;
        list.forEach((item) => {
          const v = getListItemValue(item, column);
          if (v) valueSet.add(v);
        });
      });
      return Array.from(valueSet).sort((a, b) =>
        a.localeCompare(b, undefined, { sensitivity: "base" }),
      );
    }
    return [];
  };

  const applyFilters = (rows, excludeColumnKey = null) => {
    let filtered = Array.isArray(rows) ? rows : [];

    Object.entries(filters).forEach(([columnKey, filterValue]) => {
      if (excludeColumnKey && columnKey === excludeColumnKey) return;
      if (!filterValue) return;

      const column = columns.find((col) => col.key === columnKey);
      if (!column) return;

      filtered = filtered.filter((row) => {
        switch (column.type) {
          case "text": {
            const textValue = row[columnKey]?.toString().toLowerCase() || "";
            return textValue.includes(String(filterValue).toLowerCase());
          }

          case "alphabetical": {
            const stripPunct = (s) =>
              s
                .replace(/[^\w\s]/g, "")
                .replace(/\s+/g, " ")
                .trim();
            const alphaValue = stripPunct(
              (row[columnKey]?.toString() || "").toLowerCase(),
            );
            return alphaValue.includes(
              stripPunct(String(filterValue).toLowerCase()),
            );
          }

          case "discrete":
            return String(row[columnKey]) === String(filterValue);

          case "participant": {
            const participants = row[columnKey] || [];
            return participants.some((p) => {
              const displayName =
                p.type === "contact"
                  ? `${p.first_name || ""} ${p.last_name || ""}`.trim()
                  : p.name_short;
              return displayName === filterValue;
            });
          }

          case "list": {
            if (!Array.isArray(filterValue) || filterValue.length === 0)
              return true;
            const rowList = row[columnKey];
            if (!Array.isArray(rowList) || rowList.length === 0) return false;
            const rowValues = rowList
              .map((item) => getListItemValue(item, column))
              .filter(Boolean);
            const mode = listFilterMatchMode?.[columnKey] || "all";
            if (mode === "all") {
              return filterValue.every((selected) =>
                rowValues.includes(selected),
              );
            }
            return filterValue.some((selected) => rowValues.includes(selected));
          }

          case "date":
            if (filterValue.type === "single") {
              const rowDate = parseDbDate(row[columnKey]);
              const filterDate = parseDbDate(filterValue.value);
              return !!rowDate && !!filterDate && rowDate.toDateString() === filterDate.toDateString();
            } else if (filterValue.type === "range") {
              const rowDate = parseDbDate(row[columnKey]);
              const startDate = parseDbDate(filterValue.start);
              const endDate = parseDbDate(filterValue.end);
              return !!rowDate && rowDate >= startDate && rowDate <= endDate;
            }
            return true;

          default:
            return true;
        }
      });
    });

    return filtered;
  };

  // Handle sorting
  const handleSort = (columnKey, columnType) => {
    if (sortColumn === columnKey) {
      if (columnType === "alphabetical") {
        // Cycle between: asc -> desc -> asc -> desc...
        setSortDirection(sortDirection === "asc" ? "desc" : "asc");
      } else if (columnType === "numerical") {
        // Cycle between: asc -> desc -> asc -> desc...
        setSortDirection(sortDirection === "asc" ? "desc" : "asc");
      } else if (columnType === "date") {
        // Cycle between: desc -> asc -> desc -> asc...
        setSortDirection(sortDirection === "desc" ? "asc" : "desc");
      }
    } else {
      // Different column clicked - reset previous column and start new sort
      flushSync(() => {
        setSortColumn(columnKey);
        if (columnType === "alphabetical") {
          setSortDirection("asc"); // Start with alphabetical order
        } else if (columnType === "numerical") {
          setSortDirection("asc"); // Start with smallest first
        } else if (columnType === "date") {
          setSortDirection("desc"); // Start with newest first
        } else {
          setSortDirection("asc"); // Default for other sortable columns
        }
      });
    }
  };

  // Handle filter changes
  const handleFilterChange = (columnKey, value) => {
    setFilters((prev) => ({
      ...prev,
      [columnKey]: value,
    }));
  };

  // Filter and sort data
  const processedData = useMemo(() => {
    let filtered = applyFilters(data);

    const hasPrioritySort = typeof getRowSortPriority === "function";
    const getPriority = (row) => {
      if (!hasPrioritySort) return 0;
      const raw = getRowSortPriority(row);
      const n = Number(raw);
      return Number.isFinite(n) ? n : 0;
    };

    // Apply sorting (never mutate the input array)
    if (sortColumn && sortDirection !== "none") {
      const column = columns.find((col) => col.key === sortColumn);
      if (column) {
        const sorted = [...filtered].sort((a, b) => {
          if (hasPrioritySort) {
            const pa = getPriority(a);
            const pb = getPriority(b);
            if (pa < pb) return -1;
            if (pa > pb) return 1;
          }

          let aValue = a?.[sortColumn];
          let bValue = b?.[sortColumn];

          if (column.type === "date") {
            aValue = parseDbDate(aValue)?.getTime() ?? NaN;
            bValue = parseDbDate(bValue)?.getTime() ?? NaN;
          } else if (column.type === "numerical") {
            const aNum = Number(aValue);
            const bNum = Number(bValue);
            const aOk = Number.isFinite(aNum);
            const bOk = Number.isFinite(bNum);
            if (!aOk && !bOk) return 0;
            if (!aOk) return sortDirection === "asc" ? 1 : -1;
            if (!bOk) return sortDirection === "asc" ? -1 : 1;
            aValue = aNum;
            bValue = bNum;
          } else if (
            column.type === "alphabetical" ||
            typeof aValue === "string"
          ) {
            aValue = (aValue || "").toString().toLowerCase();
            bValue = (bValue || "").toString().toLowerCase();
          }

          if (aValue < bValue) return sortDirection === "asc" ? -1 : 1;
          if (aValue > bValue) return sortDirection === "asc" ? 1 : -1;
          return 0;
        });

        return sorted;
      }
    }

    // If no active column sort, still apply the priority sort (stable)
    if (hasPrioritySort) {
      return [...filtered]
        .map((row, idx) => ({ row, idx, p: getPriority(row) }))
        .sort((a, b) => a.p - b.p || a.idx - b.idx)
        .map((x) => x.row);
    }

    // If unsorted, still return a stable copy to avoid accidental prop mutation downstream.
    return Array.isArray(filtered) ? [...filtered] : [];
  }, [
    data,
    filters,
    sortColumn,
    sortDirection,
    columns,
    listFilterMatchMode,
    getRowSortPriority,
  ]);

  // Virtualize table rows so only the rows scrolled into view are mounted in the DOM.
  const rowVirtualizer = useVirtualizer({
    count: processedData.length,
    getScrollElement: () => scrollContainerRef.current,
    estimateSize: () => 48,
    overscan: 10,
  });

  const virtualRows = rowVirtualizer.getVirtualItems();
  const totalRowsSize = rowVirtualizer.getTotalSize();
  const paddingTop = virtualRows.length > 0 ? virtualRows[0].start : 0;
  const paddingBottom =
    virtualRows.length > 0
      ? totalRowsSize - virtualRows[virtualRows.length - 1].end
      : 0;

  // Measure pinned column widths so trailing columns know how far to offset from the left.
  React.useEffect(() => {
    if (lastFixedIndex < 0) {
      setPinnedOffsets([]);
      return;
    }

    const measure = () => {
      const offsets = [];
      let acc = 0;
      for (let i = 0; i <= lastFixedIndex; i++) {
        offsets[i] = acc;
        acc += headerCellRefs.current[i]?.offsetWidth || 0;
      }
      setPinnedOffsets(offsets);
    };

    measure();

    const observer = new ResizeObserver(measure);
    headerCellRefs.current.slice(0, lastFixedIndex + 1).forEach((el) => {
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, [lastFixedIndex, columns, processedData]);

  // Notify parent component when processed data changes
  React.useEffect(() => {
    if (!onProcessedDataChange) return;

    // Guard against feedback loops where the parent re-renders and we
    // recompute a new array reference with identical contents.
    const length = processedData.length;
    const firstId = length ? (processedData[0]?.id ?? 0) : 0;
    const lastId = length ? (processedData[length - 1]?.id ?? 0) : 0;
    const signature = `${length}:${firstId}:${lastId}`;

    if (lastProcessedSignatureRef.current === signature) return;
    lastProcessedSignatureRef.current = signature;
    onProcessedDataChange(processedData);
  }, [processedData, onProcessedDataChange]);

  // Render cell content based on column type
  const renderCellContent = (row, column) => {
    const value = row[column.key];

    switch (column.type) {
      case "date":
        if (column.render) return column.render(value, row);
        // "22/10/2026 15:30" (en-GB puts a comma after the date)
        return (
          parseDbDate(value)
            ?.toLocaleString("en-GB", {
              day: "2-digit",
              month: "2-digit",
              year: "numeric",
              hour: "2-digit",
              minute: "2-digit",
            })
            .replace(", ", " ") ?? "—"
        );

      case "participant":
        const participants = value || [];
        return (
          <span>
            {participants.map((p, i) => {
              // Get display name - full name, or initials for FinanceTalking
              // staff (p.is_ftl) when the column sets ftlInitials
              let displayName;
              if (p.type === "contact") {
                const firstName = p.first_name || "";
                const lastName = p.last_name || "";
                if (column.ftlInitials && p.is_ftl) {
                  // Show initials: first letter of first name + first letter of last name
                  const firstInitial = firstName.charAt(0).toUpperCase();
                  const lastInitial = lastName.charAt(0).toUpperCase();
                  displayName = `${firstInitial}${lastInitial}`;
                } else {
                  displayName = `${firstName} ${lastName}`.trim();
                }
              } else {
                displayName = p.name_short;
              }

              return (
                <span
                  key={`${p.type}-${p.id}`}
                  style={p.is_ftl ? { fontStyle: "italic" } : undefined}
                >
                  <Link
                    to={`/${p.type}?id=${p.id}`}
                    title={
                      p.type === "contact"
                        ? `${p.first_name || ""} ${p.last_name || ""}`.trim()
                        : p.name_short
                    }
                  >
                    {typeIcons[p.type]} {displayName}
                  </Link>
                  {p.is_sender ? " (S)" : ""}
                  {i < participants.length - 1 && ", "}
                </span>
              );
            })}
          </span>
        );

      case "list": {
        const list = Array.isArray(value) ? value : [];
        const labels = list
          .map((item) => getListItemLabel(item, column))
          .filter(Boolean);
        return labels.length ? labels.join(", ") : "—";
      }

      case "text":
        if (column.truncate && value && value.length > column.truncate) {
          return (
            <span title={value}>{value.slice(0, column.truncate)}...</span>
          );
        }
        return value || "—";

      case "discrete":
        return column.render ? column.render(value, row) : value || "—";

      case "alphabetical":
        return column.render ? column.render(value, row) : value || "—";

      case "static":
        return column.render ? column.render(value, row) : value || "—";

      case "custom":
        return column.render ? column.render(value, row) : value;

      default:
        return value || "—";
    }
  };

  // Render filter input based on column type
  const renderFilter = (column) => {
    // Don't show filters for custom or static columns
    if (column.type === "custom" || column.type === "static") {
      return null;
    }

    const filterValue =
      filters[column.key] || (column.type === "list" ? [] : "");

    switch (column.type) {
      case "text":
        return (
          <input
            autoComplete="off"
            type="text"
            placeholder={`Search ${column.header.toLowerCase()}...`}
            value={filterValue}
            onChange={(e) => handleFilterChange(column.key, e.target.value)}
            className="table-filter-input"
          />
        );

      case "alphabetical":
        return (
          <input
            autoComplete="off"
            type="text"
            placeholder={`Search ${column.header.toLowerCase()}...`}
            value={filterValue}
            onChange={(e) => handleFilterChange(column.key, e.target.value)}
            className="table-filter-input"
          />
        );

      case "discrete":
      case "participant":
        const options = getUniqueValues(column.key, column.type);
        return (
          <select
            autoComplete="off"
            value={filterValue}
            onChange={(e) => handleFilterChange(column.key, e.target.value)}
            className="table-filter-select"
          >
            <option value="">All {column.header.toLowerCase()}</option>
            {options.map((option) => (
              <option key={option} value={option}>
                {column.discreteLabels
                  ? (column.discreteLabels[option] ?? option)
                  : option}
              </option>
            ))}
          </select>
        );

      case "list": {
        const rowsForOptions = applyFilters(data, column.key);
        const availableOptions = getUniqueValues(
          column.key,
          column.type,
          rowsForOptions,
        );
        const availableSet = new Set(
          Array.isArray(availableOptions) ? availableOptions : [],
        );

        const selected = Array.isArray(filterValue) ? filterValue : [];

        // Allow a column to provide an explicit full option list (e.g. all flags from /flags/),
        // while still disabling options that don't exist in the current table rows.
        const providedAllOptions = Array.isArray(column?.listFilterAllOptions)
          ? column.listFilterAllOptions
          : null;
        const options = (
          providedAllOptions ? providedAllOptions : availableOptions
        )
          .concat(selected)
          .filter(Boolean);

        // De-dupe while preserving a stable sort.
        const optionsDeduped = Array.from(new Set(options)).sort((a, b) =>
          String(a).localeCompare(String(b), undefined, {
            sensitivity: "base",
          }),
        );

        const isOpen = openListFilterKey === column.key;
        const mode = listFilterMatchMode?.[column.key] || "all";

        // Build grouped options: when multiple values share a common "XXX - " prefix,
        // show a toggleable group titled "XXX" and display only the suffix per checkbox.
        const groupedMap = new Map();
        const ungrouped = [];
        (Array.isArray(optionsDeduped) ? optionsDeduped : []).forEach(
          (optionValue) => {
            const { prefix, suffix } = splitPrefixedOption(optionValue);
            if (prefix && suffix) {
              if (!groupedMap.has(prefix)) groupedMap.set(prefix, []);
              groupedMap
                .get(prefix)
                .push({ value: optionValue, label: suffix });
              return;
            }
            ungrouped.push({ value: optionValue, label: String(optionValue) });
          },
        );

        const grouped = Array.from(groupedMap.entries())
          .map(([prefix, items]) => ({
            prefix,
            items: items
              .slice()
              .sort((a, b) =>
                a.label.localeCompare(b.label, undefined, {
                  sensitivity: "base",
                }),
              ),
          }))
          .sort((a, b) =>
            a.prefix.localeCompare(b.prefix, undefined, {
              sensitivity: "base",
            }),
          );

        // Only keep groups where there are multiple items; singletons are treated as ungrouped.
        const multiGroups = grouped.filter((g) => g.items.length > 1);
        const singletons = grouped
          .filter((g) => g.items.length === 1)
          .map((g) => ({
            value: g.items[0].value,
            label: String(g.items[0].value),
          }));

        const finalUngrouped = [...ungrouped, ...singletons].sort((a, b) =>
          a.label.localeCompare(b.label, undefined, { sensitivity: "base" }),
        );

        const isGroupOpen = (prefix) => {
          const byColumn = openListFilterGroups?.[column.key] || {};
          // Default to collapsed
          return byColumn[prefix] === true;
        };

        const toggleGroup = (prefix) => {
          setOpenListFilterGroups((prev) => {
            const prevForColumn = prev?.[column.key] || {};
            const nextOpen = !(prevForColumn[prefix] === true);
            return {
              ...prev,
              [column.key]: {
                ...prevForColumn,
                [prefix]: nextOpen,
              },
            };
          });
        };

        return (
          <div
            className="table-filter-list"
            style={{ position: "relative", display: "inline-block" }}
            ref={isOpen ? listFilterContainerRef : null}
          >
            <button
              type="button"
              className="table-filter-button"
              onClick={() => {
                const willOpen = openListFilterKey !== column.key;
                if (willOpen) {
                  // Default grouped sections to collapsed whenever the dropdown opens
                  setOpenListFilterGroups((prev) => ({
                    ...(prev || {}),
                    [column.key]: {},
                  }));
                }
                setOpenListFilterKey(willOpen ? column.key : null);
              }}
              title={
                selected.length
                  ? `Filtering by ${selected.length} item(s) (${mode.toUpperCase()})`
                  : "Filter"
              }
            >
              {column.filterIcon}
              Filter{selected.length ? ` (${selected.length})` : ""}
            </button>

            {isOpen && (
              <div
                ref={listFilterPanelRef}
                className="table-filter-dropdown"
                style={{
                  ...listFilterPanelStyle,
                  minWidth: "360px",
                  overflowY: "auto",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: "8px",
                    marginBottom: "8px",
                  }}
                >
                  <strong style={{ fontSize: "0.9em" }}>
                    Select{" "}
                    {column.header?.toString?.()?.toLowerCase?.() || "items"}
                  </strong>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    <button
                      type="button"
                      className="table-filter-dropdown-action"
                      onClick={() =>
                        setListFilterMatchMode((prev) => ({
                          ...prev,
                          [column.key]:
                            (prev?.[column.key] || "any") === "all"
                              ? "any"
                              : "all",
                        }))
                      }
                      title="Toggle match mode"
                    >
                      {mode.toUpperCase()}
                    </button>
                    <button
                      type="button"
                      className="table-filter-dropdown-action"
                      onClick={() => handleFilterChange(column.key, [])}
                      disabled={selected.length === 0}
                      title="Clear selection"
                    >
                      Clear
                    </button>
                  </div>
                </div>

                {optionsDeduped.length === 0 ? (
                  <div style={{ fontSize: "0.9em", opacity: 0.7 }}>
                    No options
                  </div>
                ) : (
                  <div
                    className="table-filter-list-options"
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "6px",
                    }}
                  >
                    {multiGroups.map((group) => {
                      const open = isGroupOpen(group.prefix);
                      const selectedCount = group.items.reduce(
                        (acc, item) =>
                          selected.includes(item.value) ? acc + 1 : acc,
                        0,
                      );
                      const allDisabled = group.items.every((item) => {
                        const checked = selected.includes(item.value);
                        const disabled =
                          !availableSet.has(item.value) && !checked;
                        return disabled;
                      });
                      return (
                        <div key={group.prefix} className="table-filter-group">
                          <button
                            type="button"
                            className="table-filter-group-toggle"
                            onClick={() => {
                              if (allDisabled) return;
                              toggleGroup(group.prefix);
                            }}
                            disabled={allDisabled}
                            title={
                              allDisabled
                                ? `${group.prefix} (no matching rows)`
                                : `Toggle ${group.prefix}`
                            }
                            style={{
                              opacity: allDisabled ? 0.5 : 1,
                              cursor: allDisabled ? "not-allowed" : "pointer",
                            }}
                          >
                            <span className="table-filter-group-indicator">
                              {open ? "▾" : "▸"}
                            </span>
                            <span className="table-filter-group-title">
                              {group.prefix}
                              {selectedCount ? ` (${selectedCount})` : ""}
                            </span>
                          </button>

                          {open && (
                            <div
                              className="table-filter-group-items"
                              style={{
                                display: "flex",
                                flexDirection: "column",
                                gap: "6px",
                              }}
                            >
                              {group.items.map((item) => {
                                const checked = selected.includes(item.value);
                                const disabled =
                                  !availableSet.has(item.value) && !checked;
                                return (
                                  <label
                                    key={item.value}
                                    className="table-filter-option"
                                    style={{
                                      opacity: disabled ? 0.5 : 1,
                                      cursor: disabled
                                        ? "not-allowed"
                                        : "pointer",
                                    }}
                                  >
                                    <Checkbox
                                      checked={checked}
                                      disabled={disabled}
                                      onChange={() => {
                                        if (disabled) return;
                                        const next = checked
                                          ? selected.filter(
                                              (v) => v !== item.value,
                                            )
                                          : [...selected, item.value];
                                        handleFilterChange(column.key, next);
                                      }}
                                      size="sm"
                                    />
                                    <span style={{ fontSize: "0.9em" }}>
                                      {item.label}
                                    </span>
                                  </label>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    })}

                    {finalUngrouped.map((item) => {
                      const checked = selected.includes(item.value);
                      const disabled =
                        !availableSet.has(item.value) && !checked;
                      return (
                        <label
                          key={String(item.value)}
                          className="table-filter-option"
                          style={{
                            opacity: disabled ? 0.5 : 1,
                            cursor: disabled ? "not-allowed" : "pointer",
                          }}
                        >
                          <Checkbox
                            checked={checked}
                            disabled={disabled}
                            onChange={() => {
                              if (disabled) return;
                              const next = checked
                                ? selected.filter((v) => v !== item.value)
                                : [...selected, item.value];
                              handleFilterChange(column.key, next);
                            }}
                            size="sm"
                          />
                          <span style={{ fontSize: "0.9em" }}>
                            {item.label}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>
        );
      }

      case "date":
        return (
          <div className="date-filter">
            <select
              autoComplete="off"
              value={filterValue?.type || ""}
              onChange={(e) => {
                const type = e.target.value;
                if (!type) {
                  handleFilterChange(column.key, "");
                } else if (type === "single") {
                  handleFilterChange(column.key, { type: "single", value: "" });
                } else if (type === "range") {
                  handleFilterChange(column.key, {
                    type: "range",
                    start: "",
                    end: "",
                  });
                }
              }}
              className="table-filter-select"
            >
              <option value="">No filter</option>
              <option value="single">Single date</option>
              <option value="range">Date range</option>
            </select>

            {filterValue?.type === "single" && (
              <input
                autoComplete="off"
                type="date"
                value={filterValue.value}
                onChange={(e) =>
                  handleFilterChange(column.key, {
                    ...filterValue,
                    value: e.target.value,
                  })
                }
                className="table-filter-input"
              />
            )}

            {filterValue?.type === "range" && (
              <div className="date-range-inputs">
                <input
                  autoComplete="off"
                  type="date"
                  placeholder="Start date"
                  value={filterValue.start}
                  onChange={(e) =>
                    handleFilterChange(column.key, {
                      ...filterValue,
                      start: e.target.value,
                    })
                  }
                  className="table-filter-input"
                />
                <input
                  autoComplete="off"
                  type="date"
                  placeholder="End date"
                  value={filterValue.end}
                  onChange={(e) =>
                    handleFilterChange(column.key, {
                      ...filterValue,
                      end: e.target.value,
                    })
                  }
                  className="table-filter-input"
                />
              </div>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  if (loading) {
    return <p>Loading...</p>;
  }

  if (data.length === 0) {
    return <p>{emptyMessage}</p>;
  }

  // Check if any column has filters - only these types render actual filter inputs
  const hasAnyFilters = columns.some((column) =>
    [
      "text",
      "alphabetical",
      "discrete",
      "participant",
      "date",
      "list",
    ].includes(column.type),
  );

  return (
    <div className={`flexible-table-container${fillPage ? " fill-page" : ""}`}>
      {/* Scrollable Table Container */}
      <div
        ref={scrollContainerRef}
        className={`scrollable-table-wrapper${fillPage ? " fill-page" : ""}`}
      >
        <table
          className="flexible-table"
          style={{ minWidth: `${tableMinWidth}px` }}
        >
          <colgroup>
            {columns.map((column, index) => (
              <col
                key={column.key}
                style={{ width: `${columnWidthPercents[index]}%` }}
              />
            ))}
          </colgroup>
          <thead>
            <tr>
              {columns.map((column, index) => {
                const filterControl = hasAnyFilters
                  ? renderFilter(column)
                  : null;
                const isPinned = lastFixedIndex >= 0 && index <= lastFixedIndex;
                const pinnedClass = isPinned
                  ? `pinned-col${index === lastFixedIndex ? " pinned-col-last" : ""}`
                  : "";
                const pinnedStyle = isPinned
                  ? {
                      position: "sticky",
                      left: `${pinnedOffsets[index] || 0}px`,
                    }
                  : undefined;

                return (
                  <th
                    key={column.key}
                    ref={(el) => {
                      headerCellRefs.current[index] = el;
                    }}
                    className={pinnedClass || undefined}
                    style={pinnedStyle}
                  >
                    <div className="header-content">
                      <div className="header-title-row">
                        <div className="header-title">{column.header}</div>
                        {(column.type === "date" ||
                          column.type === "alphabetical" ||
                          column.type === "numerical") &&
                          column.sortable !== false && (
                            <button
                              className="sort-button"
                              onClick={() =>
                                handleSort(column.key, column.type)
                              }
                              title={
                                column.type === "date"
                                  ? sortColumn === column.key &&
                                    sortDirection === "desc"
                                    ? "Sorting newest first"
                                    : sortColumn === column.key &&
                                        sortDirection === "asc"
                                      ? "Sorting oldest first"
                                      : "Click to sort"
                                  : column.type === "numerical"
                                    ? sortColumn === column.key &&
                                      sortDirection === "asc"
                                      ? "Sorting smallest first"
                                      : sortColumn === column.key &&
                                          sortDirection === "desc"
                                        ? "Sorting largest first"
                                        : "Click to sort"
                                    : sortColumn === column.key &&
                                        sortDirection === "asc"
                                      ? "Sorting A-Z"
                                      : sortColumn === column.key &&
                                          sortDirection === "desc"
                                        ? "Sorting Z-A"
                                        : "Click to sort alphabetically"
                              }
                            >
                              {sortColumn === column.key &&
                              sortDirection === "desc" ? (
                                <i className="fas fa-sort-up"></i>
                              ) : sortColumn === column.key &&
                                sortDirection === "asc" ? (
                                <i className="fas fa-sort-down"></i>
                              ) : (
                                <i className="fas fa-sort"></i>
                              )}
                            </button>
                          )}
                      </div>

                      {filterControl ? (
                        <div className="header-filter">{filterControl}</div>
                      ) : null}
                    </div>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {paddingTop > 0 && (
              <tr aria-hidden="true">
                <td
                  style={{ height: paddingTop, padding: 0, border: "none" }}
                  colSpan={columns.length}
                />
              </tr>
            )}
            {virtualRows.map((virtualRow) => {
              const index = virtualRow.index;
              const row = processedData[index];
              const extraClass =
                typeof getRowClassName === "function"
                  ? getRowClassName(row) || ""
                  : "";
              const clickableClass = onRowClick ? "clickable" : "";
              const rowClassName = [clickableClass, extraClass]
                .filter(Boolean)
                .join(" ");

              return (
                <tr
                  key={row.id || index}
                  ref={rowVirtualizer.measureElement}
                  data-index={index}
                  onClick={() => onRowClick && onRowClick(row)}
                  className={rowClassName}
                >
                  {columns.map((column, colIndex) => {
                    const isPinned =
                      lastFixedIndex >= 0 && colIndex <= lastFixedIndex;
                    const pinnedClass = isPinned
                      ? `pinned-col${colIndex === lastFixedIndex ? " pinned-col-last" : ""}`
                      : "";
                    const pinnedStyle = isPinned
                      ? {
                          position: "sticky",
                          left: `${pinnedOffsets[colIndex] || 0}px`,
                        }
                      : undefined;

                    return (
                      <td
                        key={`${row.id || index}-${column.key}`}
                        className={pinnedClass || undefined}
                        style={pinnedStyle}
                        onClick={(e) => {
                          // Stop propagation if the click target is a link or inside a link
                          if (
                            e.target.tagName === "A" ||
                            e.target.closest("a")
                          ) {
                            e.stopPropagation();
                          }
                        }}
                      >
                        {renderCellContent(row, column)}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
            {paddingBottom > 0 && (
              <tr aria-hidden="true">
                <td
                  style={{ height: paddingBottom, padding: 0, border: "none" }}
                  colSpan={columns.length}
                />
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Results count */}
      <div className="table-results-info">
        Showing {processedData.length} of {data.length} results
      </div>
    </div>
  );
};

export default Table;
